import React, { useState, useEffect, FormEvent, useCallback } from 'react';
import AllVehicleTable from './Table/AllVehicleTable';
import Pagination from 'reusable/pagination';
import axiosInstance from 'config/axiosInstance';
import {
  DialogConfig,
  DialogField,
  DialogType,
  NewDataField,
} from 'reusable/Dialog/dialogInterface';
import {
  addVehicleFields,
  allVehicleSortingOptions,
  deleteVehicleFields,
  editVehicleFields,
} from './AllVehicleFields';
import EditDialog from 'reusable/Dialog/editDialog';
import AddDialog from 'reusable/Dialog/addDialog';
import AddButton from 'reusable/addButton';
import DeleteDialog from 'reusable/Dialog/deleteDialog';
import Search from 'reusable/search';
import SortBy from 'reusable/sortby';
import Export from 'reusable/export';
import { useTranslation } from 'react-i18next';

const AllVehicleManagement: React.FC = () => {
  const { t, i18n } = useTranslation(['vehicle', 'common']);
  const [searchValue, setSearchValue] = useState('');
  const [sortValue, setSortValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [count, setCount] = useState(0);
  const rowsPerPage = 10;
  const [vehicleData, setVehicleData] = useState<any[]>([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [dialogProps, setDialogProps] = useState<DialogConfig>({
    title: '',
    subtitle: '',
    note: '',
    fields: [],
  });
  const [currentVehicleId, setCurrentVehicleId] = useState<string | null>(null);

  const fetchProfilePicture = async (driverId: string) => {
    try {
      const response = await axiosInstance.get(`/v1/drivers/${driverId}/`);
      return response.data.profile_picture;
    } catch (error) {
      console.error(
        `Failed to fetch profile picture for driver ${driverId}`,
        error
      );
      return null;
    }
  };

  const fetchData = useCallback(async () => {
    try {
      let apiUrl = `/v1/vehicles/?order_by=${sortValue}&page=${currentPage}&page_size=10&search=${searchValue}`;
      const response = await axiosInstance.get(apiUrl);
      const fetchedVehicleData = response.data.results;

      const updatedVehicleData = await Promise.all(
        fetchedVehicleData.map(async (vehicle: any) => {
          if (vehicle.last_used_by?.id) {
            const profilePicture = await fetchProfilePicture(
              vehicle.last_used_by.id
            );
            return {
              ...vehicle,
              last_used_by: {
                ...vehicle.last_used_by,
                profile_picture: profilePicture,
              },
            };
          }
          return vehicle;
        })
      );

      setVehicleData(updatedVehicleData);
      setCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, [searchValue, sortValue, currentPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openDialog = (dialogConfig: DialogConfig, dialogType: DialogType) => {
    setDialogProps(dialogConfig);
    if (dialogType === DialogType.Add) setIsAddDialogOpen(true);
    if (dialogType === DialogType.Edit) setIsEditDialogOpen(true);
    if (dialogType === DialogType.Delete) setIsDeleteDialogOpen(true);
  };

  const closeDialog = () => {
    setIsAddDialogOpen(false);
    setIsEditDialogOpen(false);
    setIsDeleteDialogOpen(false);
    setCurrentVehicleId(null);
  };

  const handleDialog = async (dialogType: DialogType, id?: string) => {
    if (dialogType === DialogType.Add) {
      openDialog(
        {
          title: `${t('all-vehicle.add-vehicle.title')}`,
          fields: addVehicleFields(t),
        },
        DialogType.Add
      );
    } else if (
      (dialogType === DialogType.Edit || dialogType === DialogType.Delete) &&
      id
    ) {
      try {
        const vehicleResponse = await axiosInstance.get(`/v1/vehicles/${id}/`);
        const vehicleData = vehicleResponse.data;
        setCurrentVehicleId(id);
        if (dialogType === DialogType.Edit) {
          openDialog(
            {
              title: `${t('all-vehicle.edit-vehicle.title')}`,
              fields: editVehicleFields(t, vehicleData),
            },
            DialogType.Edit
          );
        } else if (dialogType === DialogType.Delete) {
          openDialog(
            {
              title: `${t('all-vehicle.delete-vehicle.title')}`,
              subtitle: `${t('all-vehicle.delete-vehicle.info')}`,
              note: `${t('all-vehicle.delete-vehicle.info2')}`,
              fields: deleteVehicleFields(t, vehicleData),
            },
            DialogType.Delete
          );
        }
      } catch (error) {
        console.error('Failed to fetch vehicle data', error);
      }
    }
  };

  const handleAddVehicle = async (
    e: FormEvent<Element>,
    newData: NewDataField
  ) => {
    e.preventDefault();

    try {
      const response = await axiosInstance.post('/v1/vehicles/', newData);
      const newVehicle = response.data;

      setIsAddDialogOpen(false);
      setVehicleData((prevData) => [...prevData, newVehicle]);
      window.location.reload();
    } catch (err: any) {
      console.log('Failed to add vehicle:', err);
    }
  };

  const handleEditVehicle = async (
    e: FormEvent<Element>,
    updatedData: NewDataField & { id: string }
  ) => {
    e.preventDefault();

    try {
      if (updatedData) {
        const response = await axiosInstance.patch(
          `/v1/vehicles/${updatedData.id}/`,
          updatedData
        );

        setVehicleData((prevData) =>
          prevData.map((vehicle) =>
            vehicle.id === updatedData.id ? response.data : vehicle
          )
        );
      }

      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Failed to update vehicle:', error);
    }
  };

  const handleDeleteVehicle = async (id: string) => {
    try {
      await axiosInstance.delete(`/v1/vehicles/${id}/`);
      setVehicleData(vehicleData.filter((vehicle) => vehicle.id !== id));
      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error('Failed to delete vehicle:', error);
    }
  };

  const handleSortChange = (sortValue: string) => {
    setSortValue(sortValue);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handlePageChange = (value: number) => {
    setCurrentPage(value);
  };

  return (
    <div>
      <div className="flex flex-row justify-between space-y-2">
        <div className="flex w-2/5 justify-start">
          <Search
            placeholder={t('all-vehicle.table.search')}
            onSearchChange={handleSearchChange}
          />
        </div>
        <div className="flex flex-row space-x-2 items-center">
          <SortBy
            options={allVehicleSortingOptions(t)}
            defaultValue=""
            onChange={handleSortChange}
          />
          <AddButton
            label={t('all-vehicle.table.add-vehicle')}
            onClick={() => handleDialog(DialogType.Add)}
          />
          <AddDialog
            isOpen={isAddDialogOpen}
            onClose={closeDialog}
            handleSubmit={handleAddVehicle}
            title={dialogProps.title}
            subtitle={dialogProps.subtitle ?? ''}
            confirmLabel={t('all-vehicle.add-vehicle.save')}
            cancelLabel={t('all-vehicle.add-vehicle.cancel')}
            fields={dialogProps.fields}
          />
          <EditDialog
            isOpen={isEditDialogOpen}
            onClose={closeDialog}
            handleSubmit={(e: any, updatedData: any) =>
              handleEditVehicle(e, {
                ...updatedData,
                id: currentVehicleId ?? '',
              })
            }
            title={dialogProps.title}
            subtitle={dialogProps.subtitle ?? ''}
            confirmLabel={t('all-vehicle.edit-vehicle.save')}
            cancelLabel={t('all-vehicle.edit-vehicle.cancel')}
            fields={dialogProps.fields}
            vehicleId={currentVehicleId ?? ''}
          />
          <DeleteDialog
            isOpen={isDeleteDialogOpen}
            onClose={closeDialog}
            handleSubmit={(e: any) =>
              handleDeleteVehicle(currentVehicleId || '')
            }
            title={dialogProps.title}
            subtitle={dialogProps.subtitle ?? ''}
            note={dialogProps.note ?? ''}
            confirmLabel={t('all-vehicle.delete-vehicle.delete')}
            cancelLabel={t('all-vehicle.delete-vehicle.cancel')}
            fields={dialogProps.fields}
            vehicleId={currentVehicleId ?? ''}
          />

          <Export
            data={vehicleData}
            exportFields={[
              { header: 'Plate Number', field: 'plate_number' },
              { header: 'Car Model', field: 'car_model' },
              { header: 'Last used by', field: 'last_used_by.name' },
              { header: 'Last used by', field: 'last_used_by.phone' },
              { header: 'Added Date', field: 'create_time' },
              { header: 'Last Inspection', field: 'last_inspection' },
            ]}
          />
        </div>
      </div>

      <AllVehicleTable
        vehicles={vehicleData}
        onEdit={handleDialog.bind(null, DialogType.Edit)}
        onDelete={handleDialog.bind(null, DialogType.Delete)}
      />

      <div>
        <Pagination
          allRows={count}
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default AllVehicleManagement;
